import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail } from "lucide-react";

const TermsOfService = () => {
  const navigate = useNavigate();
  const lastUpdated = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="glass-effect border-b border-white/20">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="sm"
              className="hover:bg-white/50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <div className="flex items-center gap-3">
              <img 
                src="/images/robot-genie-logo.png" 
                alt="Kids Genie" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <span className="text-xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Kids Genie AI, LLC
                </span>
                <div className="text-xs font-medium text-gray-600 -mt-1">AI & Coding Academy</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto max-w-4xl px-6 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Terms of Service
            </h1>
            <p className="text-sm text-gray-600">Last Updated: {lastUpdated}</p>
          </div>

          {/* Contact Box */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-8">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-indigo-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 mb-1">
                  For support or legal questions:
                </p>
                <a 
                  href="mailto:support@kidsgenie-ai-academy.com" 
                  className="text-sm text-indigo-600 hover:underline"
                >
                  support@kidsgenie-ai-academy.com
                </a>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="prose max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              Welcome to Kids Genie AI & Coding Academy ("we", "us", or "our"). By using our website, learning platform, or services, you ("Parent/Guardian" or "User") agree to the terms described below. Our platform is designed for children, but parents or legal guardians must create and manage accounts. Children may interact with the platform only under parental permission. If you do not agree with these terms, please do not use the platform.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Who We Are</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Company Name:</strong> Kids Genie AI LLC</li>
              <li><strong>Website:</strong> <a href="https://kidsgenie-ai-academy.com" className="text-indigo-600 hover:underline">https://kidsgenie-ai-academy.com</a></li>
            </ul>
            <p className="text-gray-700 mt-4">
              We provide interactive, fully automated AI-guided digital learning experiences.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Parent/Guardian Responsibility & Registration</h2>
            <p className="text-gray-700 mb-4">
              Our platform is designed for children ages 6-12. In compliance with the Children's Online Privacy Protection Act (COPPA) and the EU General Data Protection Regulation (GDPR-K), only parents or legal guardians may create accounts and make purchases.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4">
              <p className="text-sm font-semibold text-blue-900 mb-2">Registration Requirements:</p>
              <ul className="list-disc pl-6 space-y-1 text-sm text-blue-800">
                <li>You must be <strong>18 years or older</strong> to create an account</li>
                <li>You must be the <strong>legal parent or guardian</strong> of the child being registered</li>
                <li>You must provide your <strong>full name</strong> and <strong>email address</strong></li>
                <li>You must provide your <strong>child's name</strong> and <strong>age</strong> (6-12 years)</li>
                <li>You must confirm your guardian status and accept our Terms of Service and Privacy Policy</li>
              </ul>
            </div>
            
            <p className="text-gray-700 mb-4">
              Parents are responsible for supervising and approving their child's use of the platform. Parents may review progress, request data deletion, or close accounts at any time by contacting us at <a href="mailto:support@kidsgenie-ai-academy.com" className="text-indigo-600 hover:underline">support@kidsgenie-ai-academy.com</a>.
            </p>
            <p className="text-gray-700 font-semibold">
              By creating an account, you confirm that you are 18 years or older and legally able to consent on behalf of the child. You also acknowledge that you have read and understood our Privacy Policy regarding the collection of children's data.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Use of the Platform</h2>
            <p className="text-gray-700 mb-4">
              Children use the platform to interact with AI-generated lessons, activities, and feedback. The platform does not include live human instruction. You agree that:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>The AI content is for learning support only and should not replace parental guidance.</li>
              <li>Your child's behavior and interactions remain your responsibility.</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. AI-Generated Content and Limitations</h2>
            <p className="text-gray-700 mb-4">Our platform uses AI models to generate:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Learning explanations</li>
              <li>Coding guidance</li>
              <li>Personalized feedback</li>
            </ul>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
              <p className="text-sm font-semibold text-yellow-900 mb-2">Important Notice:</p>
              <p className="text-sm text-yellow-800">
                While we aim for accuracy and educational quality, AI content may sometimes contain mistakes, inaccurate explanations, or suggestions requiring review.
              </p>
            </div>
            <p className="text-gray-700 mt-4">
              You agree to use the platform as a supplementary educational tool. We are not liable for outcomes based on AI-generated suggestions.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Payments and Subscriptions</h2>
            <p className="text-gray-700 mb-4">Payments are processed securely through:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Stripe</li>
              <li>PayPal</li>
            </ul>
            <p className="text-gray-700 mt-4">
              We do not store credit card information. Subscriptions may renew automatically unless cancelled. You may cancel at any time through your account settings.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Prohibited Use</h2>
            <p className="text-gray-700 mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Share account login credentials outside your household</li>
              <li>Attempt to reverse-engineer or copy the platform's content or AI systems</li>
              <li>Use the platform for commercial or unauthorized teaching purposes</li>
            </ul>
            <p className="text-gray-700 mt-4 font-semibold">
              Accounts violating these rules may be suspended or closed.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Intellectual Property</h2>
            <p className="text-gray-700">
              All content on the platform, including lessons, activities, branding, and AI-generated educational materials, is owned by Kids Genie AI LLC. You may not copy, redistribute, or resell platform content.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Cancellation and Refunds</h2>
            <p className="text-gray-700">
              If you are unsatisfied, you may request cancellation or a refund depending on the policy active at your time of purchase. To request help, email:{" "}
              <a href="mailto:support@kidsgenie-ai-academy.com" className="text-indigo-600 hover:underline">
                support@kidsgenie-ai-academy.com
              </a>
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Data Privacy</h2>
            <p className="text-gray-700">
              We follow COPPA and GDPR requirements. For full details, refer to our{" "}
              <button onClick={() => navigate('/privacy')} className="text-indigo-600 hover:underline font-semibold">
                Privacy Policy
              </button>
              . Parents may request account deletion or data removal at any time.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Disclaimer of Warranty</h2>
            <p className="text-gray-700 mb-4">
              The platform is provided "as is" without guarantees of performance or outcomes. We are not responsible for:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Internet access issues</li>
              <li>Device limitations</li>
              <li>Learning outcomes or academic results</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              To the maximum extent permitted by law, Kids Genie AI LLC is not liable for:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Losses from reliance on AI-generated content</li>
              <li>Decisions or actions taken based on learning suggestions</li>
              <li>Any indirect, incidental, or consequential damages</li>
            </ul>
            <p className="text-gray-700 mt-4 font-semibold">
              Parents agree to use judgment in reviewing and guiding their child's learning.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Changes to These Terms</h2>
            <p className="text-gray-700">
              We may update these Terms occasionally. When we do, we will post the updated date at the top.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. Contact</h2>
            <p className="text-gray-700">
              For support or legal questions, email us at:{" "}
              <a href="mailto:support@kidsgenie-ai-academy.com" className="text-indigo-600 hover:underline font-semibold">
                support@kidsgenie-ai-academy.com
              </a>
            </p>
            <p className="text-gray-700 mt-4 font-medium">
              Thank you for choosing Kids Genie AI & Coding Academy!
            </p>
          </div>

          {/* Footer Actions */}
          <div className="mt-12 pt-8 border-t flex flex-wrap gap-4">
            <Button
              onClick={() => window.print()}
              variant="outline"
              className="rounded-full"
            >
              Print This Document
            </Button>
            <Button
              onClick={() => navigate('/privacy')}
              variant="outline"
              className="rounded-full"
            >
              View Privacy Policy
            </Button>
            <Button
              onClick={() => navigate('/contact')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full"
            >
              Contact Support
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;